import {useEffect, useState} from "react";
import {addMinutes, subHours} from "@ots-frontend/ots-data"
import {Badge, Group, Paper, Slider, Stack, Text} from "@mantine/core";
import {FormattedDateTime, getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";

export type HeatmapTimelineProps = {
  initialStartTime: Date | number;
  initialEndTime?: Date | number;
  onEndTimeChange: (endTime: Date | number) => void;
}

export const HeatmapTimeline = (props: HeatmapTimelineProps) => {
  const {
    initialStartTime,
    initialEndTime,
    onEndTimeChange,
  } = props;

  const [startTime, _setStartTime] = useState<Date | number>(initialStartTime);
  const [endTime, _setEndTime] = useState<Date | number>(initialEndTime || new Date());

  const totalMinutes = Math.round((new Date(endTime).getTime() - new Date(initialStartTime).getTime()) / 60000);

  const minutesSinceStart = Math.max(
    0,
    Math.min(
      totalMinutes,
      Math.round((new Date(endTime).getTime() - new Date(initialStartTime).getTime()) / 60000)
    )
  );

  const [value, setValue] = useState<number>(minutesSinceStart);

  useEffect(() => {
    if (initialEndTime) {
      const mins = Math.round(
        (new Date(initialEndTime).getTime() - new Date(startTime).getTime()) / 60000
      );
      setValue(Math.max(0, Math.min(totalMinutes, mins)));
    }
  }, [initialEndTime, startTime, totalMinutes]);

  const handleChange = (mins: number) => {
    setValue(mins);
    const newEndTime = addMinutes(startTime, mins);
    onEndTimeChange(newEndTime);
  };

  const handleChangeEnd = (mins: number) => {
    const snapped = Math.round(mins / 15) * 15;
    handleChange(snapped);
  };

  const currentTime = addMinutes(startTime, value);

  return (
    <Stack m="xs" spacing="xs">
      <Group spacing="xs" >
        <Text size="xs" fw={600}>
          Service Event Timeline
        </Text>
        <Badge size="xs" variant="filled" color="blue">
          <FormattedDateTime date={currentTime}/>
        </Badge>
      </Group>

      <Slider
        value={value}
        onChange={handleChange}
        onChangeEnd={handleChangeEnd}
        min={0}
        max={totalMinutes}
        step={15}
        label={null}
        styles={{
          track: {backgroundColor: '#e0e0e0'},
          bar: {background: 'linear-gradient(to right, #228be6, #15aabf)'},
          thumb: {borderWidth: 3, width: 24, height: 24},
        }}
      />

      <Group position="apart" opacity={0.7}>
        <Text size="xs">
          <FormattedDateTime date={startTime} />
        </Text>
        <Text size="xs">
          <FormattedDateTime date={endTime} />
        </Text>
      </Group>
    </Stack>
  );
};

